// Base URL for API requests. Defaults to the production API but can be
// overridden at build time via the `VITE_API_BASE_URL` environment variable.
// This allows the frontend to talk to either the deployed Cloudflare Worker
// backend or a local server depending on the deployment environment.
export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL !== undefined
    ? import.meta.env.VITE_API_BASE_URL
    : "https://api.capsulechichub.com";

// Customer-facing Google OAuth client id. Distinct from the admin
// backoffice client — they validate against different audiences on the
// server. Unset in local builds / when accounts are not being worked on;
// the login page surfaces a helpful message instead of a broken button.
export const GOOGLE_CUSTOMER_CLIENT_ID =
  (import.meta.env.VITE_GOOGLE_CUSTOMER_CLIENT_ID as string | undefined) ?? "";

// Customer-facing Microsoft (Entra ID) application client id. Configured
// for personal accounts only (consumers tenant). Empty string disables the
// button, mirroring the Google client id behaviour.
export const MICROSOFT_CUSTOMER_CLIENT_ID =
  (import.meta.env.VITE_MICROSOFT_CUSTOMER_CLIENT_ID as string | undefined) ?? "";

// Customer-facing Apple *Services ID* (not the mobile bundle id). Apple issues
// separate client ids per platform: the iOS app authenticates as the bundle id
// `com.capsulechichub.app`, the website as the Services ID
// `com.capsulechichub.web`. Both end up as the `aud` claim on the identity
// token the Worker validates, so the backoffice must accept both audiences
// before this is set — empty string keeps the button hidden, mirroring the
// Microsoft client id behaviour.
export const APPLE_CUSTOMER_CLIENT_ID =
  (import.meta.env.VITE_APPLE_CUSTOMER_CLIENT_ID as string | undefined) ?? "";

// Apple validates the Sign in with Apple return URL against the exact string
// registered on the Services ID, and in popup mode its origin must equal
// `window.location.origin` or the SDK never resolves. Kept as a constant so
// the Pages Function that backs the path and this config can't drift.
export const APPLE_REDIRECT_PATH = "/auth/apple-redirect";

// Number of products to display per page on the Shop view. This value is shared
// between the client pagination UI and server API responses to keep them in sync.
export const PRODUCTS_PER_PAGE = 12;

export const CATEGORIES = [
  "Clothing",
  "Accessories", 
  "Jewelry",
  "Bags",
  "Shoes",
  "Beauty"
];

export const SORT_OPTIONS = [
  { value: "marias-picks", label: "Maria's Picks" },
  { value: "newest", label: "Newest" },
  { value: "price-low", label: "Price: Low to High" },
  { value: "price-high", label: "Price: High to Low" }
];

export const PRESS_LOGOS: { src: string; alt: string; text: string }[] = [
  {
    src: "https://cdn.capsulechichub.com/featured-bbc.avif",
    alt: "BBC Ask the Experts feature",
    text: "Featured on BBC's\n'Ask the Experts'",
  },
];

export const CLIENT_PHOTOS: { src: string; alt: string }[] = [
  { src: "https://cdn.capsulechichub.com/clients/client-01-sm.avif", alt: "Maria styling a client on a shopping appointment" },
  { src: "https://cdn.capsulechichub.com/clients/client-02-sm.avif", alt: "Maria styling a client on a shopping appointment" },
  { src: "https://cdn.capsulechichub.com/clients/client-03-sm.avif", alt: "Maria styling a client on a shopping appointment" },
  { src: "https://cdn.capsulechichub.com/clients/client-04-sm.avif", alt: "Maria styling a client on a shopping appointment" },
  { src: "https://cdn.capsulechichub.com/clients/client-05-sm.avif", alt: "Maria styling a client on a shopping appointment" },
  { src: "https://cdn.capsulechichub.com/clients/client-06-sm.avif", alt: "Maria styling a client on a shopping appointment" },
  { src: "https://cdn.capsulechichub.com/clients/client-07-sm.avif", alt: "Maria styling a client on a shopping appointment" },
  { src: "https://cdn.capsulechichub.com/clients/client-08-sm.avif", alt: "Maria styling a client on a shopping appointment" },
  { src: "https://cdn.capsulechichub.com/clients/client-09-sm.avif", alt: "Maria styling a client on a shopping appointment" },
  { src: "https://cdn.capsulechichub.com/clients/client-10-sm.avif", alt: "Maria styling a client on a shopping appointment" },
  { src: "https://cdn.capsulechichub.com/clients/client-11-sm.avif", alt: "Maria styling a client on a shopping appointment" },
  { src: "https://cdn.capsulechichub.com/clients/client-12-sm.avif", alt: "Maria styling a client on a shopping appointment" },
  { src: "https://cdn.capsulechichub.com/clients/client-13-sm.avif", alt: "Maria styling a client on a shopping appointment" },
  { src: "https://cdn.capsulechichub.com/clients/client-14-sm.avif", alt: "Maria styling a client on a shopping appointment" },
  { src: "https://cdn.capsulechichub.com/clients/client-15-sm.avif", alt: "Maria styling a client on a shopping appointment" },
];

// MAR-130 / MAR-167: app download banner, iOS and Android.
//
// ON BY DEFAULT. The banner shipped dark in MAR-130 because version 1.0.0 was
// WAITING_FOR_REVIEW and APP_STORE_URL 404d, so it needed an opt-in. The app
// is live (both the /gb/ and /us/ storefronts answer 200) and has been
// shipping updates since, so the opt-in became the only reason it had never
// been seen. MAR-167 inverted it.
//
// KILL SWITCH, not an opt-in: the banner is on unless
// VITE_APP_BANNER_ENABLED is set to exactly "false". Anything else, including
// the far more common case of the variable not being set at all, leaves it on.
// So production, preview deploys and local dev all show it with no
// environment variable configured anywhere.
//
// To turn it off without a code change: set VITE_APP_BANNER_ENABLED=false in
// Cloudflare Pages, Settings, Environment variables (Production, and Preview
// if you want to kill it there too), then re-run the latest deployment. That
// is the lever to pull if the App Store or Google Play listing ever goes
// down, because a banner pointing at a 404 is worse than no banner. To turn it back on,
// unset it or set it to anything other than "false", and redeploy.
//
// The default deliberately lives here rather than in a Pages environment
// variable so it also covers preview deploys and local dev, and so changing
// it goes through the normal merge-to-main deploy path with a review.
export const APP_BANNER_ENABLED =
  (import.meta.env.VITE_APP_BANNER_ENABLED as string | undefined) !== "false";

// Capsule Chic Hub on the App Store (UK storefront). Apple resolves /gb/ to
// the visitor's own storefront, so this is safe for non-UK visitors too.
export const APP_STORE_URL = "https://apps.apple.com/gb/app/id6779920283";

// Capsule Chic Hub on Google Play. Play has no storefront path segment: the
// listing localises itself to the visitor, so one URL serves everyone. Adding
// `&hl=en_GB` would force British English on every reader, so it is left off.
// The package name is the same bundle id the iOS app uses, from mobile/app.json.
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.capsulechichub.app";
